import iniciativaVideoImg from 'assets/img/elbb/iniciativa_video.png';
import { useAuth } from 'context/AuthContext';
import React, { useEffect, useRef, useState } from 'react';
import { BsEmojiSmile } from 'react-icons/bs';
import { FaTrophy } from 'react-icons/fa';
import { HiUsers } from 'react-icons/hi2';
import { IoChevronDown } from 'react-icons/io5';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import { RiUserStarLine } from 'react-icons/ri';
import {
  registrarElogio,
  registrarFavorito,
  registrarIniciativas
} from 'services/iniciativa-action.service';
import { IniciativaFormatada } from 'types/iniciativa';
import { ModalIniciativa } from '../../Iniciativas/components/ModalIniciativa';
import './IniciativaCardElbb.scss';

type IniciativaCardElbbProps = IniciativaFormatada & {
  /** Sobrescreve o texto da label da media-frame (default: "CLASSIFICADA ETAPA X"). */
  classificadaLabel?: React.ReactNode;
  /** Sobrescreve o ícone da label da media-frame (default: troféu). */
  classificadaIcon?: React.ReactNode;
  /** Quando informado, substitui a descricao pela lista de indicados. */
  topIndicados?: Array<{ posicao: number; nome: string }>;
};


export const IniciativaCardElbb: React.FC<IniciativaCardElbbProps> = (props) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isComplimentOpen, setIsComplimentOpen] = useState(false);
  const [selectedCompliment, setSelectedCompliment] = useState<string>(props.usuarioElogio || '');
  const complimentRef = useRef<HTMLDivElement>(null);

  const [isFavorited, setIsFavorited] = useState(props.usuarioFavoritou);
  const [loadingAction, setLoadingAction] = useState(false);

  const complimentOptions = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setIsComplimentOpen(false);
      }
    };

    if (isComplimentOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isComplimentOpen]);

  const handleComplimentSelect = async (compliment: string) => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      setSelectedCompliment(compliment);
      setIsComplimentOpen(false);

      await registrarElogio(user._id, {
        id_iniciativa: props.id,
        elogio: compliment
      });
    } catch (error) {
      setSelectedCompliment('');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      const novoStatus = !isFavorited;

      await registrarFavorito(user._id, {
        id_iniciativa: props.id,
        ativo: novoStatus
      });

      setIsFavorited(novoStatus);
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  const handleOpenDetails = async () => {
    setIsModalOpen(true);

    if (!user?._id) return;

    try {
      await registrarIniciativas(user._id, props.id);
    } catch (error) {
    }
  };

  const {
    titulo,
    descricao,
    texto,
    autores,
    categoria,
    corCategoria,
    videoUrl,
    originalVideoUrl,
    classificadaEtapa1,
    classificadaEtapa2
  } = props;

  const showClassificadaEtapa2 = Boolean(classificadaEtapa2);
  const showClassificadaEtapa1 = Boolean(classificadaEtapa1) && !showClassificadaEtapa2;
  const showClassificada = showClassificadaEtapa1 || showClassificadaEtapa2;
  const cardClassName = [
    'iniciativa-card-elbb',
    showClassificada ? 'iniciativa-card-elbb--classificada' : '',
    showClassificadaEtapa2 ? 'iniciativa-card-elbb--classificada-etapa2' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const tagElement = (
    <div
      className={`iniciativa-card-elbb__tag tag-${corCategoria}`}
      tabIndex={0}
      title={categoria}
      aria-label={`Categoria: ${categoria}`}
    >
      {categoria}
    </div>
  );

  const mediaElement = videoUrl ? (
    <a
      href={originalVideoUrl || videoUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
      aria-label={`Assistir ao vídeo da iniciativa ${titulo} (abre em nova aba)`}
    >
      <img
        src={iniciativaVideoImg}
        alt=""
        aria-hidden="true"
        className="iniciativa-card-elbb__image"
      />
    </a>
  ) : null;

  return (
    <>
    <div className={cardClassName} role="article" aria-label={`Iniciativa: ${titulo}`}>
      {videoUrl && (
        showClassificada ? (
          <div className="iniciativa-card-elbb__media-frame">
            <div className="iniciativa-card-elbb__classificada-header">
              <span className="iniciativa-card-elbb__classificada-label">
                {props.classificadaIcon ?? <FaTrophy size={20} aria-hidden="true" />}
                {props.classificadaLabel ??
                  (showClassificadaEtapa2 ? 'CLASSIFICADA ETAPA 2' : 'CLASSIFICADA ETAPA 1')}
              </span>
              {tagElement}
            </div>
            <div className="iniciativa-card-elbb__image-container">{mediaElement}</div>
          </div>
        ) : (
          <div className="iniciativa-card-elbb__image-container">
            {mediaElement}
            {tagElement}
          </div>
        )
      )}

      <div className="iniciativa-card-elbb__content">
        {/* <h3 className="iniciativa-card-elbb__category" tabIndex={0}>{titulo}</h3> */}
        <h4 className="iniciativa-card-elbb__title" tabIndex={0}>{titulo}</h4>
        {props.topIndicados !== undefined ? (
          <div className="iniciativa-card-elbb__top-indicados-wrapper">
            <span className="iniciativa-card-elbb__top-indicados-label" tabIndex={0}>Indicados</span>
            {props.topIndicados.length === 0 ? (
              <p className="iniciativa-card-elbb__top-indicados-empty" tabIndex={0}>
                Ainda sem indicações
              </p>
            ) : (
              <ul
                className="iniciativa-card-elbb__top-indicados"
                aria-label="Indicados"
              >
                {props.topIndicados.map((p) => (
                  <li
                    key={p.posicao}
                    className="iniciativa-card-elbb__top-indicados-item"
                  >
                    <RiUserStarLine
                      className="iniciativa-card-elbb__top-indicados-icon"
                      size={16}
                      aria-hidden="true"
                    />
                    <span className="iniciativa-card-elbb__top-indicados-nome" tabIndex={0}>{p.nome}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="iniciativa-card-elbb__text" tabIndex={0}>{descricao}</p>
        )}

        <div className="iniciativa-card-elbb__author" tabIndex={0} aria-label={`Autores: ${autores}`}>
          <HiUsers size={16} aria-hidden="true" />
          <span>{autores}</span>
        </div>

        <div className="iniciativa-card-elbb__action-bar">
          <div className="iniciativa-card-elbb__action-left">
            <div className="iniciativa-card-elbb__icon-wrapper">
              <BsEmojiSmile size={20} aria-hidden="true" style={{ color: selectedCompliment ? '#4A65FA' : '#666666' }} />
              {props.totalElogios > 0 && (
                <span className="iniciativa-card-elbb__badge" aria-label={`${props.totalElogios} elogios`}>{props.totalElogios}</span>
              )}
            </div>
            <div className="iniciativa-card-elbb__compliment-container" ref={complimentRef}>
              <button
                className="iniciativa-card-elbb__compliment-dropdown"
                onClick={() => !loadingAction && setIsComplimentOpen(!isComplimentOpen)}
                aria-expanded={isComplimentOpen}
                aria-haspopup="listbox"
                aria-label={selectedCompliment ? `Elogio selecionado: ${selectedCompliment}` : 'Faça um elogio'}
                disabled={loadingAction}
                style={{
                  borderColor: selectedCompliment ? '#4A65FA' : '#E0E7FF',
                  color: selectedCompliment ? '#4A65FA' : '#666666'
                }}
              >
                <span>{selectedCompliment || 'Faça um elogio!'}</span>
                <IoChevronDown size={16} aria-hidden="true" />
              </button>
              {isComplimentOpen && !loadingAction && (
                <div className="iniciativa-card-elbb__compliment-menu" role="listbox" aria-label="Opções de elogio">
                  {complimentOptions.map((compliment) => (
                    <button
                      key={compliment}
                      className="iniciativa-card-elbb__compliment-option"
                      role="option"
                      aria-selected={selectedCompliment === compliment}
                      onClick={() => handleComplimentSelect(compliment)}
                    >
                      {compliment}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="iniciativa-card-elbb__action-right">
            <div className="iniciativa-card-elbb__icon-wrapper">
              <button
                className="iniciativa-card-elbb__icon-btn"
                aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
                aria-pressed={isFavorited}
                onClick={handleToggleFavorite}
                disabled={loadingAction}
                style={{
                  opacity: loadingAction ? 0.6 : 1,
                  color: isFavorited ? '#4A65FA' : '#666666'
                }}
              >
                {isFavorited ? <MdBookmark size={20} aria-hidden="true" /> : <MdBookmarkBorder size={20} aria-hidden="true" />}
              </button>
              {props.totalFavoritos > 0 && (
                <span className="iniciativa-card-elbb__badge" aria-label={`${props.totalFavoritos} favoritos`}>{props.totalFavoritos}</span>
              )}
            </div>
          </div>
        </div>

        <button
          className="iniciativa-card-elbb__button"
          onClick={handleOpenDetails}
          aria-label={`Ver mais detalhes da iniciativa ${titulo}`}
        >
          VER MAIS DETALHES
        </button>
      </div>
    </div>

    <ModalIniciativa
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      iniciativa={props}
    />
    </>
  );
};
